<template>
  <div class="print-format-tab pa-6">
    <v-card class="format-card elevation-0">
      <!-- Header -->
      <div class="format-header pa-4">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon size="24" class="mr-3" color="primary">mdi-printer-settings</v-icon>
            <span class="format-section-title">Prilagodba Formata Ispisa</span>
          </div>
          <v-chip color="info" outlined small>
            <v-icon left small>mdi-information</v-icon>
            Po printeru
          </v-chip>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Printer Selector -->
      <div class="printer-selector-section pa-6">
        <v-select
          v-model="selectedPrinterId"
          :items="printers"
          :loading="loadingPrinters"
          item-text="name"
          item-value="id"
          label="Odaberite printer"
          prepend-inner-icon="mdi-printer"
          outlined
          dense
          hide-details
          class="printer-selector"
          @change="onPrinterChange"
        >
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center">
              <v-icon small class="mr-2" color="primary">mdi-printer</v-icon>
              <span class="font-weight-medium">{{ item.name }}</span>
              <span v-if="item.library" class="ml-2 text--secondary caption">
                ({{ item.library }})
              </span>
            </div>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-icon>
              <v-icon small color="primary">mdi-printer</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.ip_address">
                {{ item.ip_address }} | {{ item.library || 'BIXOLON' }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-select>

        <v-alert
          v-if="!selectedPrinterId"
          type="info"
          text
          dense
          class="mt-4 mb-0"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-information</v-icon>
            <span>Odaberite printer za prikaz i uređivanje postavki formata ispisa</span>
          </div>
        </v-alert>
      </div>

      <!-- Loading State -->
      <div v-if="loadingFormat" class="pa-6 text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
        ></v-progress-circular>
        <div class="mt-3 text--secondary">Učitavanje postavki...</div>
      </div>

      <!-- Format Sections -->
      <div v-else-if="selectedPrinterId && printFormat" class="format-sections pa-6">
        <v-expansion-panels
          v-model="openPanels"
          accordion
          class="mb-6"
        >
          <!-- Header Section -->
          <print-section-customizer
            v-model="printFormat.header"
            title="Zaglavlje"
            description="Naziv stola i broj narudžbe"
            icon="mdi-format-title"
            icon-color="primary"
            :printer-width="printerWidthNumber"
          />

          <!-- Metadata Section -->
          <print-section-customizer
            v-model="printFormat.metadata"
            title="Metapodaci"
            description="Operater, datum i vrijeme narudžbe"
            icon="mdi-information-outline"
            icon-color="info"
            :printer-width="printerWidthNumber"
          />

          <!-- Courses Section -->
          <print-section-customizer
            v-model="printFormat.courses"
            title="Slijedovi"
            description="Zaglavlja slijedova (== PREDJELO ==, == GLAVNO JELO ==)"
            icon="mdi-format-list-numbered"
            icon-color="success"
            :printer-width="printerWidthNumber"
          />

          <!-- Items Section -->
          <print-section-customizer
            v-model="printFormat.items"
            title="Stavke"
            description="Sve stavke narudžbe (i unutar slijedova i obične)"
            icon="mdi-food"
            icon-color="orange"
            :printer-width="printerWidthNumber"
          />

          <!-- Menu Items Section -->
          <print-section-customizer
            v-model="printFormat.menuItems"
            title="Meni Stavke"
            description="Struktura menija sa podstavkama (npr. Dnevni meni)"
            icon="mdi-menu"
            icon-color="deep-purple"
            :printer-width="printerWidthNumber"
          />

          <!-- Notes Section -->
          <print-section-customizer
            v-model="printFormat.notes"
            title="Napomene"
            description="Napomene na stavkama (npr. - Bez luka, - Bez sira)"
            icon="mdi-note-text"
            icon-color="amber"
            :printer-width="printerWidthNumber"
          />

          <!-- Footer Section -->
          <print-section-customizer
            v-model="printFormat.footer"
            title="Podnožje"
            description="Bilješke i dodatne informacije na kraju ispisa"
            icon="mdi-text-box-outline"
            icon-color="grey"
            :printer-width="printerWidthNumber"
          />
        </v-expansion-panels>

        <!-- Action Buttons -->
        <v-card flat class="action-buttons-card pa-4">
          <div class="d-flex flex-wrap justify-center align-center gap-3">
            <v-btn
              color="info"
              outlined
              large
              @click="openPreview"
              :disabled="saving || testPrinting"
            >
              <v-icon left>mdi-eye</v-icon>
              Pregledaj Ispis
            </v-btn>

            <v-btn
              color="primary"
              outlined
              large
              @click="testPrint"
              :loading="testPrinting"
              :disabled="saving"
            >
              <v-icon left>mdi-printer-check</v-icon>
              Testiraj Ispis
            </v-btn>

            <v-btn
              color="success"
              large
              @click="saveFormat"
              :loading="saving"
              :disabled="testPrinting"
            >
              <v-icon left>mdi-content-save</v-icon>
              Spremi Postavke
            </v-btn>
          </div>

          <v-alert
            v-if="hasUnsavedChanges"
            type="warning"
            text
            dense
            class="mt-4 mb-0"
          >
            <v-icon class="mr-2" small>mdi-alert</v-icon>
            Imate nespremljene promjene. Kliknite "Spremi Postavke" za trajno spremanje.
          </v-alert>
        </v-card>
      </div>
    </v-card>

    <!-- Preview Dialog -->
    <print-preview-dialog
      v-if="showPreview"
      v-model="showPreview"
      :print-format="printFormat"
      :printer-width="printerWidthNumber"
    />
  </div>
</template>

<script>
import { df } from '@/plugins/firebase'
import { getDefaultPrintFormat, loadPrintFormat } from '@/libs/bixolon/order'
import { printOrder } from '@/libs/bixolon/order'
import { initPrintManager } from '@/libs/jsPrintManager/utils'
import state from '@/state'
import PrintSectionCustomizer from './PrintSectionCustomizer'
import PrintPreviewDialog from './PrintPreviewDialog'

export default {
  name: 'PrintFormatTab',

  components: {
    PrintSectionCustomizer,
    PrintPreviewDialog
  },

  props: {
    locationId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      printers: [],
      loadingPrinters: false,
      selectedPrinterId: null,
      printFormat: null,
      loadingFormat: false,
      saving: false,
      testPrinting: false,
      hasUnsavedChanges: false,
      openPanels: null, // All panels closed by default
      showPreview: false,
      printersListener: null
    }
  },

  computed: {
    selectedPrinter () {
      return this.printers.find(p => p.id === this.selectedPrinterId) || {}
    },

    printerWidthNumber () {
      // Convert printer width to number, default to 80
      const width = this.selectedPrinter.width
      if (!width) return 80
      return typeof width === 'string' ? parseInt(width, 10) : width
    }
  },

  watch: {
    locationId: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.loadPrinters()
        }
      }
    },

    printFormat: {
      deep: true,
      handler () {
        if (this.printFormat && !this.loadingFormat) {
          this.hasUnsavedChanges = true
        }
      }
    }
  },

  mounted () {
    // Initialize printer libraries for test print functionality
    this.initPrinterLibraries()
  },

  beforeDestroy () {
    // Clean up Firestore listener
    if (this.printersListener) {
      this.printersListener()
    }
  },

  methods: {
    /**
     * Initialize printer libraries (JSPM and Neodynamic)
     * Required for test print functionality to work
     */
    initPrinterLibraries () {
      // Set printer library in state based on available printer
      // Default to UNIVERSAL for this settings page
      state.setPrinterLib('UNIVERSAL')

      // Initialize JSPM (JSPrintManager) for UNIVERSAL library
      try {
        initPrintManager(
          () => {
            console.log('[PrintFormatTab] JSPM initialized successfully')
          },
          () => {
            console.warn('[PrintFormatTab] JSPM initialization failed - test print may not work')
          }
        )
      } catch (error) {
        console.warn('[PrintFormatTab] Error initializing JSPM:', error)
      }

      // Load required libraries for UNIVERSAL printer
      // These are needed for character encoding support
      const loadScript = (src, name) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = src
          script.async = true
          script.onload = () => {
            console.log(`[PrintFormatTab] ✅ ${name} loaded successfully`)
            resolve()
          }
          script.onerror = () => {
            console.error(`[PrintFormatTab] ❌ Failed to load ${name}`)
            reject(new Error(`Failed to load ${name}`))
          }
          document.head.appendChild(script)
        })
      }

      // Load libraries in sequence: cptable -> cputils -> JSESCPOSBuilder
      const loadPrinterLibraries = async () => {
        try {
          // Load cptable.js (character code page tables)
          if (!window.cptable) {
            await loadScript(
              'https://firebasestorage.googleapis.com/v0/b/ai-fisk-stage/o/javascript%2Fcptable.js?alt=media&token=2bbbaafa-5866-4acd-8e7f-7ad119399288',
              'cptable.js'
            )
          }

          // Load cputils.js (code page utilities)
          if (!window.cputils) {
            await loadScript(
              'https://firebasestorage.googleapis.com/v0/b/ai-fisk-stage/o/javascript%2Fcputils.js?alt=media&token=aa7abbec-5afa-43d1-b052-e0df13527090',
              'cputils.js'
            )
          }

          // Load JSESCPOSBuilder
          if (!window.Neodynamic || !window.Neodynamic.JSESCPOSBuilder) {
            await loadScript(
              'https://firebasestorage.googleapis.com/v0/b/ai-fisk-stage/o/javascript%2FJSESCPOSBuilder.js?alt=media&token=711f55f8-3128-473c-9e83-52c472ac0a42',
              'JSESCPOSBuilder'
            )
          }

          console.log('[PrintFormatTab] ✅ All printer libraries loaded successfully')
        } catch (error) {
          console.error('[PrintFormatTab] ❌ Error loading printer libraries:', error)
        }
      }

      // Start loading libraries
      loadPrinterLibraries()
    },

    async loadPrinters () {
      if (!this.locationId) {
        return
      }

      this.loadingPrinters = true

      try {
        // Clean up previous listener
        if (this.printersListener) {
          this.printersListener()
        }

        // Set up real-time listener
        const query = df.collection(`location_printers/${this.locationId}/printers`)
        this.printersListener = query.onSnapshot((snapshot) => {
          this.printers = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))

          // Sort printers by name
          this.printers.sort((a, b) => (a.name || '').localeCompare(b.name || ''))

          this.loadingPrinters = false
        })
      } catch (error) {
        console.error('Error loading printers:', error)
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri učitavanju printera',
          color: 'error'
        })
        this.loadingPrinters = false
      }
    },

    async onPrinterChange () {
      if (!this.selectedPrinterId) {
        this.printFormat = null
        return
      }

      this.loadingFormat = true
      this.hasUnsavedChanges = false

      try {
        // Load print format for selected printer
        this.printFormat = await loadPrintFormat(this.selectedPrinterId, this.locationId)
        console.log('Loaded print format:', this.printFormat)
      } catch (error) {
        console.error('Error loading print format:', error)
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri učitavanju formata',
          color: 'error'
        })
        // Fallback to default
        this.printFormat = getDefaultPrintFormat()
      } finally {
        this.loadingFormat = false
      }
    },

    async saveFormat () {
      if (!this.selectedPrinterId || !this.printFormat) {
        return
      }

      this.saving = true

      try {
        const docRef = df.doc(`location_printers/${this.locationId}/printers/${this.selectedPrinterId}`)

        await docRef.set({
          print_format: this.printFormat
        }, { merge: true })

        this.hasUnsavedChanges = false

        this.$root.$emit('show-snackbar', {
          text: 'Format ispisa uspješno spremljen',
          color: 'success'
        })
      } catch (error) {
        console.error('Error saving print format:', error)
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri spremanju formata',
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    openPreview () {
      this.showPreview = true
    },

    async testPrint () {
      console.log('[PrintFormatTab] Test print started')

      if (!this.selectedPrinterId || !this.printFormat) {
        console.warn('[PrintFormatTab] Missing printer or format')
        return
      }

      // Check if printer library is loaded for UNIVERSAL printers
      const printerData = this.selectedPrinter
      console.log('[PrintFormatTab] Selected printer:', printerData)

      if (printerData.library === 'UNIVERSAL') {
        console.log('[PrintFormatTab] Checking UNIVERSAL library availability...')
        if (!window.Neodynamic || !window.Neodynamic.JSESCPOSBuilder) {
          console.error('[PrintFormatTab] UNIVERSAL library not available')
          this.$root.$emit('show-snackbar', {
            text: 'Printer library nije učitana. Molimo pričekajte nekoliko sekundi i pokušajte ponovno.',
            color: 'warning'
          })
          return
        }
        console.log('[PrintFormatTab] UNIVERSAL library available ✓')
      }

      this.testPrinting = true

      try {
        console.log('[PrintFormatTab] Creating mock order data...')

        // Create mock order data
        const mockTable = {
          name: 'STOL 5',
          id: 'mock-table-1'
        }

        const mockOrder = {
          id: 'mock-order-1',
          order_number: 123
        }

        const mockItems = [
          {
            name: 'PIZZA MARGHERITA',
            quantity: 2,
            notes: []
          },
          {
            name: 'SALATA CEZAR SA PILETINOM I PARMEZANOM',
            quantity: 1,
            notes: [{ orderId: 'mock-order-1', message: 'Bez luka molim' }]
          },
          {
            name: 'COCA COLA 0.33L',
            quantity: 3,
            notes: []
          }
        ]

        const mockNote = {
          message: 'Hitan stol - molim brzo'
        }

        console.log('[PrintFormatTab] Calling printOrder with format:', this.printFormat)

        // Call printOrder with format override
        const result = await printOrder(
          mockItems,
          printerData,
          mockTable,
          mockOrder,
          undefined,
          undefined,
          mockNote,
          undefined,
          this.printFormat // formatOverride parameter
        )

        console.log('[PrintFormatTab] printOrder result:', result)

        if (result && result.success) {
          console.log('[PrintFormatTab] Test print successful ✓')
          this.$root.$emit('show-snackbar', {
            text: 'Test print uspješno poslan na printer',
            color: 'success'
          })
        } else {
          console.error('[PrintFormatTab] Print failed:', result)
          throw new Error(result?.error || 'Print failed')
        }
      } catch (error) {
        console.error('[PrintFormatTab] Error test printing:', error)
        this.$root.$emit('show-snackbar', {
          text: `Greška pri test printanju: ${error.message || 'Nepoznata greška'}`,
          color: 'error'
        })
      } finally {
        console.log('[PrintFormatTab] Test print finished')
        this.testPrinting = false
      }
    }
  }
}
</script>

<style scoped>
.print-format-tab {
  max-width: 1200px;
  margin: 0 auto;
}

.format-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 4px 20px rgba(0, 179, 190, 0.08) !important;
  overflow: hidden;
}

.format-header {
  background: rgba(0, 179, 190, 0.03) !important;
}

.format-section-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
  letter-spacing: 0.3px !important;
}

.printer-selector-section {
  background: rgba(255, 255, 255, 0.5) !important;
  border-bottom: 1px solid rgba(0, 179, 190, 0.1);
}

.printer-selector {
  max-width: 600px;
}

.printer-selector >>> .v-input__control {
  background: rgba(255, 255, 255, 0.95) !important;
}

.format-sections {
  background: white;
}

.action-buttons-card {
  background: rgba(0, 179, 190, 0.03) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.1) !important;
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 600px) {
  .print-format-tab {
    padding: 12px;
  }

  .format-header {
    padding: 16px;
  }

  .format-section-title {
    font-size: 16px !important;
  }

  .printer-selector {
    max-width: 100%;
  }

  .action-buttons-card .v-btn {
    width: 100%;
    margin-bottom: 8px;
  }

  .d-flex.flex-wrap {
    flex-direction: column;
  }
}
</style>
